/**
 * Google OAuth scope resolver.
 *
 * Maps per-service access modes (stored per connected account in
 * the connection's `google/1` metadata) to the exact set of Google OAuth scope URIs to request
 * during authorization. Each account is resolved independently — one agent's
 * work mailbox may hold send scopes while its personal one holds read-only.
 *
 * This is the security-critical boundary: only scopes returned here are granted
 * to the runtime's access token, and Google's API enforces them regardless of
 * what commands the agent attempts.
 *
 * Gmail caveats:
 *   `gmail.modify` includes reading plus mailbox organization actions
 *   (labels, read/unread state, archive/trash). Use it for "manage",
 *   and for higher cumulative modes that also need to inspect mail.
 *
 *   Drafts vs send at the scope level:
 *   Gmail does not expose a scope that grants "drafts but not send". The
 *   `gmail.compose` scope (drafts + send) is the closest to "drafts", and
 *   `gmail.send` alone is send-without-read. Since higher modes are cumulative,
 *   both "drafts" and "send" request [modify, compose] — the two modes are
 *   scope-identical, so Google cannot tell them apart and a drafts-mode token
 *   will happily send.
 *
 *   That makes the platform gate the ENTIRE boundary for drafts mode, not
 *   defense-in-depth: `checkGoogleCommandPolicy`
 *   (apps/runtime/src/tools/google/run.ts) parses each `gog` invocation before
 *   execution and hard-blocks any send-capable subcommand unless the mode is
 *   `send`, recording a `policy_violation` row in `integration_events`. The
 *   runtime additionally injects `GOG_GMAIL_NO_SEND=1` for every non-send mode,
 *   so the CLI refuses too. Prompt discipline is a third layer, not the control.
 *
 *   Change the scope table above and you may be moving that boundary — the send
 *   verbs the gate knows about are pinned to a specific gog version and were
 *   bypassable via command aliases until #653/#656.
 *
 * Adding a scope:
 *   Google's verification of our app covers a fixed list of scopes, and
 *   requesting one outside it makes the consent screen call the app
 *   unverified. Every scope must be recorded in SCOPE_VERIFICATION below as
 *   verified or pending. Read that comment before adding one.
 */

// Scope URIs — Google's official OAuth scopes.
// Reference: https://developers.google.com/identity/protocols/oauth2/scopes

const GMAIL_READONLY = "https://www.googleapis.com/auth/gmail.readonly";
const GMAIL_MODIFY   = "https://www.googleapis.com/auth/gmail.modify";
const GMAIL_COMPOSE  = "https://www.googleapis.com/auth/gmail.compose";

const DRIVE_READONLY = "https://www.googleapis.com/auth/drive.readonly";
const DRIVE_FILE     = "https://www.googleapis.com/auth/drive.file";
const DRIVE_FULL     = "https://www.googleapis.com/auth/drive";

const CALENDAR_READONLY = "https://www.googleapis.com/auth/calendar.readonly";
const CALENDAR_FULL     = "https://www.googleapis.com/auth/calendar";

const CONTACTS_READONLY = "https://www.googleapis.com/auth/contacts.readonly";
const CONTACTS_FULL     = "https://www.googleapis.com/auth/contacts";

const SHEETS_READONLY = "https://www.googleapis.com/auth/spreadsheets.readonly";
const SHEETS_FULL     = "https://www.googleapis.com/auth/spreadsheets";

const DOCS_READONLY = "https://www.googleapis.com/auth/documents.readonly";
const DOCS_FULL     = "https://www.googleapis.com/auth/documents";

// Forms has no responses WRITE scope — respondents submit responses, the API
// only reads them — so both modes carry responses.readonly and differ only in
// whether the form itself (structure, publish state) may be changed.
//
// Every Forms method is also authorized by the Drive scopes (`drive` and
// `drive.file` for all of them, `drive.readonly` for the reads), so an account
// with Drive enabled can already reach Forms through that grant. These scopes
// are what let an account WITHOUT Drive use Forms, and they are what the mode
// means to the settings panel and the agent.
const FORMS_BODY_READONLY      = "https://www.googleapis.com/auth/forms.body.readonly";
const FORMS_BODY               = "https://www.googleapis.com/auth/forms.body";
const FORMS_RESPONSES_READONLY = "https://www.googleapis.com/auth/forms.responses.readonly";

// Always included when at least one service is non-disabled, so we can know
// which account granted consent (→ the connection's authorized_email), which
// is also that connection's identity.
const OIDC_SCOPES = ["openid", "email"] as const;

// --- Google's verification of each scope ---
//
// THE SCOPES GOOGLE HAS APPROVED FOR OUR OAUTH APP, and the ones it has not.
//
// The app (GCP project botler-487210) passed OAuth verification with CASA
// Tier 2. That approval covers a fixed list of scopes. Request even ONE scope
// outside the list and Google's consent screen calls the app unverified, for
// every user asked for that scope. This has happened: the Forms scopes shipped
// in #919 (2026-09-09) without being in the approved set, and prod reported
// that the app "doesn't appear to be verified anymore".
//
// Every scope the service tables below can request needs an entry here. The
// tables are typed by this object's keys, so a scope without one does not
// compile. Before adding a scope, or moving one to "verified":
//   - "verified": the Google Auth Platform → Data access page for
//     botler-487210 shows it as APPROVED. Adding it to the consent-screen
//     config is not approval. Check the Console.
//   - "pending": Google has not approved it. The settings panel warns before
//     sending anyone to a consent screen that asks for it
//     (`pendingVerificationServices`).
// scopes.test.ts pins both lists, so changing one here means changing the
// test too. That is on purpose: the reviewer has to see the change.
//
// The OAuth client these scopes are requested through (GOOGLE_OAUTH_CLIENT_ID,
// the "Squidler" client in botler-487210) is marked AGENTIC in Google Auth
// Platform since 2026-10-20: Google's self-declaration that a client serves an
// AI agent, required from 2026-11-11 to call the Workspace MCP servers and
// permanent on existing clients from 2026-11-12. Sign-in with Google goes
// through a separate, non-agentic client that only Auth0 knows, so nothing in
// this registry concerns it. Which client is which: docs/provider-apps.md.
export type ScopeVerification = "verified" | "pending";

const SCOPE_VERIFICATION = {
  [OIDC_SCOPES[0]]: "verified",
  [OIDC_SCOPES[1]]: "verified",
  [GMAIL_READONLY]: "verified",
  [GMAIL_MODIFY]: "verified",
  [GMAIL_COMPOSE]: "verified",
  [DRIVE_READONLY]: "verified",
  [DRIVE_FILE]: "verified",
  [DRIVE_FULL]: "verified",
  [CALENDAR_READONLY]: "verified",
  [CALENDAR_FULL]: "verified",
  [CONTACTS_READONLY]: "verified",
  [CONTACTS_FULL]: "verified",
  [SHEETS_READONLY]: "verified",
  [SHEETS_FULL]: "verified",
  [DOCS_READONLY]: "verified",
  [DOCS_FULL]: "verified",
  // Added after verification was submitted (#919). They are sensitive, not
  // restricted, so approving them is a scope addition with no new CASA
  // assessment.
  [FORMS_BODY_READONLY]: "pending",
  [FORMS_BODY]: "pending",
  [FORMS_RESPONSES_READONLY]: "pending",
} as const satisfies Record<string, ScopeVerification>;

type GoogleScope = keyof typeof SCOPE_VERIFICATION;

/** Every scope we can request, and whether Google has approved it. */
export const SCOPE_VERIFICATIONS: Readonly<Record<GoogleScope, ScopeVerification>> = SCOPE_VERIFICATION;

const VERIFIED_SCOPES: ReadonlySet<string> = new Set(
  Object.entries(SCOPE_VERIFICATION).filter(([, v]) => v === "verified").map(([scope]) => scope),
);

/**
 * The scopes in `scopes` that Google has not approved for our app. A scope
 * missing from the registry counts as unapproved.
 */
export const unverifiedScopes = (scopes: readonly string[]): string[] =>
  scopes.filter((s) => !VERIFIED_SCOPES.has(s));

// --- Types ---

export type GmailMode    = "disabled" | "read" | "manage" | "drafts" | "send";
export type DriveMode    = "disabled" | "read" | "file" | "write";
export type CalendarMode = "disabled" | "read" | "write";
export type ContactsMode = "disabled" | "read" | "write";
export type SheetsMode   = "disabled" | "read" | "write";
export type DocsMode     = "disabled" | "read" | "write";
export type FormsMode    = "disabled" | "read" | "write";

export interface GoogleAccess {
  readonly gmail:    GmailMode;
  readonly drive:    DriveMode;
  readonly calendar: CalendarMode;
  readonly contacts: ContactsMode;
  readonly sheets:   SheetsMode;
  readonly docs:     DocsMode;
  readonly forms:    FormsMode;
}

// --- Mode vocabulary ---
//
// Stated once, here, because two apps validate against it and a mode one of
// them accepts but the other does not is a security bug in one direction or a
// broken settings page in the other. It used to live in the web app alone,
// which was fine while `google_connections` carried CHECK constraints that
// repeated it; on `integration_connections` the modes are keys of a jsonb
// document, and nothing at the schema level stops a value this list does not
// know.

export const GMAIL_MODES = ["disabled", "read", "manage", "drafts", "send"] as const satisfies readonly GmailMode[];
export const DRIVE_MODES = ["disabled", "read", "file", "write"] as const satisfies readonly DriveMode[];
export const READ_WRITE_MODES = ["disabled", "read", "write"] as const satisfies readonly CalendarMode[];

const pick = <T extends string>(value: unknown, allowed: readonly T[]): T | null =>
  allowed.find((m) => m === value) ?? null;

/**
 * Validate an untrusted access payload: every field a mode that service
 * accepts, or null. For INPUT — the authorize and settings routes answer 400
 * on null rather than guessing which scope the user meant.
 */
export const parseGoogleAccess = (input: unknown): GoogleAccess | null => {
  if (!input || typeof input !== "object") return null;
  const v: Record<string, unknown> = { ...input };
  const gmail = pick(v.gmail, GMAIL_MODES);
  const drive = pick(v.drive, DRIVE_MODES);
  const calendar = pick(v.calendar, READ_WRITE_MODES);
  const contacts = pick(v.contacts, READ_WRITE_MODES);
  const sheets = pick(v.sheets, READ_WRITE_MODES);
  const docs = pick(v.docs, READ_WRITE_MODES);
  const forms = pick(v.forms, READ_WRITE_MODES);
  if (!gmail || !drive || !calendar || !contacts || !sheets || !docs || !forms) return null;
  return { gmail, drive, calendar, contacts, sheets, docs, forms };
};

/**
 * Read STORED modes, each one it cannot recognise as `disabled`. For READERS
 * — a mode outside the vocabulary grants nothing, rather than failing the
 * whole connection or guessing generously.
 */
export const googleAccessOrDisabled = (input: unknown): GoogleAccess => {
  const v: Record<string, unknown> = input !== null && typeof input === "object" ? { ...input } : {};
  return {
    gmail: pick(v.gmail, GMAIL_MODES) ?? "disabled",
    drive: pick(v.drive, DRIVE_MODES) ?? "disabled",
    calendar: pick(v.calendar, READ_WRITE_MODES) ?? "disabled",
    contacts: pick(v.contacts, READ_WRITE_MODES) ?? "disabled",
    sheets: pick(v.sheets, READ_WRITE_MODES) ?? "disabled",
    docs: pick(v.docs, READ_WRITE_MODES) ?? "disabled",
    forms: pick(v.forms, READ_WRITE_MODES) ?? "disabled",
  };
};

// --- Per-service mappers (pure, table-driven) ---

const GMAIL_SCOPES: Record<GmailMode, readonly GoogleScope[]> = {
  disabled: [],
  read:     [GMAIL_READONLY],
  manage:   [GMAIL_MODIFY],
  drafts:   [GMAIL_MODIFY, GMAIL_COMPOSE],
  send:     [GMAIL_MODIFY, GMAIL_COMPOSE],
};

const DRIVE_SCOPES: Record<DriveMode, readonly GoogleScope[]> = {
  disabled: [],
  read:     [DRIVE_READONLY],
  file:     [DRIVE_FILE],
  write:    [DRIVE_FULL],
};

const CALENDAR_SCOPES: Record<CalendarMode, readonly GoogleScope[]> = {
  disabled: [],
  read:     [CALENDAR_READONLY],
  write:    [CALENDAR_FULL],
};

const CONTACTS_SCOPES: Record<ContactsMode, readonly GoogleScope[]> = {
  disabled: [],
  read:     [CONTACTS_READONLY],
  write:    [CONTACTS_FULL],
};

const SHEETS_SCOPES: Record<SheetsMode, readonly GoogleScope[]> = {
  disabled: [],
  read:     [SHEETS_READONLY],
  write:    [SHEETS_FULL],
};

const DOCS_SCOPES: Record<DocsMode, readonly GoogleScope[]> = {
  disabled: [],
  read:     [DOCS_READONLY],
  write:    [DOCS_FULL],
};

const FORMS_SCOPES: Record<FormsMode, readonly GoogleScope[]> = {
  disabled: [],
  read:     [FORMS_BODY_READONLY, FORMS_RESPONSES_READONLY],
  write:    [FORMS_BODY, FORMS_RESPONSES_READONLY],
};

// --- Public API ---

/** Google reports the OIDC email scope under its long name. */
const normalizeScope = (scope: string): string =>
  scope === "https://www.googleapis.com/auth/userinfo.email" ? "email" : scope;

/** True if every service is disabled — nothing to authorize. */
export const isEveryServiceDisabled = (access: GoogleAccess): boolean =>
  access.gmail    === "disabled" &&
  access.drive    === "disabled" &&
  access.calendar === "disabled" &&
  access.contacts === "disabled" &&
  access.sheets   === "disabled" &&
  access.docs     === "disabled" &&
  access.forms    === "disabled";

/**
 * Resolve a GoogleAccess config to the full scope URI list to request.
 *
 * Returns an empty array when every service is disabled (no OAuth flow needed).
 * Otherwise includes OIDC identity scopes plus every service scope.
 *
 * The output is deduplicated and sorted for stable comparison against
 * the connection's granted_scopes.
 */
export const resolveScopes = (access: GoogleAccess): readonly string[] => {
  if (isEveryServiceDisabled(access)) return [];

  const all = [
    ...OIDC_SCOPES,
    ...GMAIL_SCOPES[access.gmail],
    ...DRIVE_SCOPES[access.drive],
    ...CALENDAR_SCOPES[access.calendar],
    ...CONTACTS_SCOPES[access.contacts],
    ...SHEETS_SCOPES[access.sheets],
    ...DOCS_SCOPES[access.docs],
    ...FORMS_SCOPES[access.forms],
  ];

  return [...new Set(all)].sort();
};

/**
 * WHAT THE GRANT ACTUALLY COVERS.
 *
 * Google's consent screen lets the user untick individual scopes, and the token
 * that comes back then carries fewer scopes than were requested. The stored
 * modes were derived from the REQUEST, never from the grant, so a connection
 * could say `gmail = drafts` while `granted_scopes` held no Gmail scope at all
 * — prod, 2026-09-03: the agent's system prompt promised Gmail search and
 * drafts, every call was a 403, and the agent could only tell the user the
 * platform's own statement was wrong (report f499005b).
 *
 * `grantedAccess` walks each service's modes from the intended one downward and
 * keeps the first whose scopes the grant fully contains; a service none of
 * whose modes are covered is `disabled`. It never widens: a mode above the
 * intent is not considered even when the grant would allow it. Gmail
 * `drafts`/`send` share a scope set, so the intent decides between them —
 * that distinction is platform policy, not consent.
 */
const MODE_LADDERS: { readonly [K in keyof GoogleAccess]: readonly GoogleAccess[K][] } = {
  gmail:    ["send", "drafts", "manage", "read"],
  drive:    ["write", "read", "file"],
  calendar: ["write", "read"],
  contacts: ["write", "read"],
  sheets:   ["write", "read"],
  docs:     ["write", "read"],
  forms:    ["write", "read"],
};

const SCOPES_BY_SERVICE: { readonly [K in keyof GoogleAccess]: Record<GoogleAccess[K], readonly string[]> } = {
  gmail: GMAIL_SCOPES,
  drive: DRIVE_SCOPES,
  calendar: CALENDAR_SCOPES,
  contacts: CONTACTS_SCOPES,
  sheets: SHEETS_SCOPES,
  docs: DOCS_SCOPES,
  forms: FORMS_SCOPES,
};

const coveredMode = <K extends keyof GoogleAccess>(
  service: K, intent: GoogleAccess[K], granted: ReadonlySet<string>,
): GoogleAccess[K] => {
  if (intent === "disabled") return intent;
  const ladder = MODE_LADDERS[service] as readonly GoogleAccess[K][];
  const scopesOf = SCOPES_BY_SERVICE[service] as Record<GoogleAccess[K], readonly string[]>;
  const candidates = ladder.slice(ladder.indexOf(intent));
  const covered = candidates.find((mode) => scopesOf[mode].every((s) => granted.has(s)));
  return covered ?? ("disabled" as GoogleAccess[K]);
};

export const grantedAccess = (intent: GoogleAccess, granted: readonly string[]): GoogleAccess => {
  const set = new Set(granted.map(normalizeScope));
  return {
    gmail:    coveredMode("gmail", intent.gmail, set),
    drive:    coveredMode("drive", intent.drive, set),
    calendar: coveredMode("calendar", intent.calendar, set),
    contacts: coveredMode("contacts", intent.contacts, set),
    sheets:   coveredMode("sheets", intent.sheets, set),
    docs:     coveredMode("docs", intent.docs, set),
    forms:    coveredMode("forms", intent.forms, set),
  };
};

/**
 * The services whose selected mode asks Google for a scope it has not approved
 * for our app. Connecting with any of them switched on gets the "unverified
 * app" consent screen, so the settings panel names them before sending the
 * user to Google.
 */
export const pendingVerificationServices = (access: GoogleAccess): (keyof GoogleAccess)[] =>
  (Object.keys(MODE_LADDERS) as (keyof GoogleAccess)[]).filter((k) => {
    const scopesOf = SCOPES_BY_SERVICE[k] as Record<GoogleAccess[typeof k], readonly string[]>;
    return unverifiedScopes(scopesOf[access[k]]).length > 0;
  });

/** Services whose effective mode is below the intended one — what to tell the user. */
export const declinedServices = (intent: GoogleAccess, effective: GoogleAccess): (keyof GoogleAccess)[] =>
  (Object.keys(MODE_LADDERS) as (keyof GoogleAccess)[]).filter((k) => intent[k] !== effective[k]);

/**
 * Compare intent (user settings) against granted (snapshot from token).
 * Returns "widen" if intent needs MORE than granted, "narrow" if LESS, "match"
 * if identical, or "drift" if neither is a subset of the other (treat as narrow
 * for re-auth purposes — old token has scopes the user no longer wants).
 */
export type ScopeDiff = "match" | "widen" | "narrow" | "drift";

/**
 * What saving a change to one connection's access modes does to its grant.
 *
 * ONE definition, because two consumers act on it and they must not be able to
 * disagree: the settings panel warns "saving this disconnects the account",
 * and the PATCH route actually deletes the connection. A hand-mirrored copy of
 * this rule means a future verdict from `diffScopes` either deletes a live
 * grant with no warning shown, or shows a red warning for a save that does
 * nothing.
 *
 *   "noop"   — same scopes; save in place.
 *   "widen"  — needs scopes the stored grant lacks; re-authorization.
 *   "revoke" — takes scopes away. Google has already issued the grant and
 *              cannot narrow it, so the only way to honour the user's intent
 *              is to discard it and have them connect again.
 *
 * Compares intent to intent (mode set vs mode set), never against the scopes
 * Google reported granting — Google normalizes some scope names (`email` →
 * userinfo.email), which reads as drift on a clean widen.
 */
export type AccessChange = "noop" | "widen" | "revoke";

export const classifyAccessChange = (
  current: GoogleAccess,
  next: GoogleAccess,
): AccessChange => {
  const diff = diffScopes(resolveScopes(next), resolveScopes(current));
  if (diff === "match") return "noop";
  if (diff === "widen") return "widen";
  return "revoke"; // "narrow" and "drift" both drop something the grant holds
};

export const diffScopes = (
  intent: readonly string[],
  granted: readonly string[],
): ScopeDiff => {
  const normalizedIntent = intent.map(normalizeScope);
  const normalizedGranted = granted.map(normalizeScope);
  const intentSet = new Set(normalizedIntent);
  const grantedSet = new Set(normalizedGranted);

  const intentSubsetOfGranted = normalizedIntent.every((s) => grantedSet.has(s));
  const grantedSubsetOfIntent = normalizedGranted.every((s) => intentSet.has(s));

  if (intentSubsetOfGranted && grantedSubsetOfIntent) return "match";
  if (intentSubsetOfGranted) return "narrow";   // granted has extra
  if (grantedSubsetOfIntent) return "widen";    // intent has extra
  return "drift";                                // neither way is a subset
};
